import type {
  OwnerIncidentRecord,
  OwnerPortalProfile,
  OwnerProfileUpdateRequest,
  TagType,
} from "@/lib/api/client"

export interface OwnerPortalFormState {
  allergies: string
  assigneeEmail: string
  assigneeName: string
  assigneePhone: string
  bloodType: string
  color: string
  companyName: string
  emergencyContactName: string
  emergencyContactPhone: string
  fleetName: string
  make: string
  medicalConditions: string
  medications: string
  model: string
  ownerEmail: string
  ownerName: string
  ownerPhone: string
  plateNumber: string
  responderNotes: string
  vehicleType: string
  vin: string
  year: string
}

export interface OwnerDetailRow {
  label: string
  value: string
}

export interface OwnerContactLabels {
  description: string
  email: string
  name: string
  phone: string
}

export interface OwnerOtpChallenge {
  debugOtp: string
  expiresAt: number
  phoneNumber: string
  resendAvailableAt: number
  tag: string
  tagType: TagType | null
  verificationId: string
}

export function buildPublicGrievanceHref(input: {
  caseType: string
  reason?: string | null
  source?: string | null
  tag?: string | null
}) {
  const searchParams = new URLSearchParams()

  if (input.caseType.trim()) {
    searchParams.set("caseType", input.caseType.trim())
  }

  if (input.source?.trim()) {
    searchParams.set("source", input.source.trim())
  }

  if (input.reason?.trim()) {
    searchParams.set("reason", input.reason.trim())
  }

  if (input.tag?.trim()) {
    searchParams.set("tag", input.tag.trim())
  }

  const search = searchParams.toString()
  return search ? `/grievance?${search}` : "/grievance"
}

export function createEmptyOwnerPortalFormState(): OwnerPortalFormState {
  return {
    allergies: "",
    assigneeEmail: "",
    assigneeName: "",
    assigneePhone: "",
    bloodType: "",
    color: "",
    companyName: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    fleetName: "",
    make: "",
    medicalConditions: "",
    medications: "",
    model: "",
    ownerEmail: "",
    ownerName: "",
    ownerPhone: "",
    plateNumber: "",
    responderNotes: "",
    vehicleType: "",
    vin: "",
    year: "",
  }
}

export function createOwnerPortalFormState(
  profile: OwnerPortalProfile | null
): OwnerPortalFormState {
  if (!profile) {
    return createEmptyOwnerPortalFormState()
  }

  const tagType = profile.tagType ?? "vehicle_sticker"
  const contact = profile.contact
  const assignment = profile.assignment
  const vehicle = profile.vehicle
  const medicalProfile = profile.medicalProfile
  const isFleet = tagType === "fleet_tag"

  return {
    allergies: medicalProfile?.allergies ?? "",
    assigneeEmail: assignment?.assigneeEmail ?? "",
    assigneeName: assignment?.assigneeName ?? "",
    assigneePhone: assignment?.assigneePhone ?? "",
    bloodType: medicalProfile?.bloodType ?? "",
    color: vehicle?.color ?? "",
    companyName: isFleet ? vehicle?.companyName ?? "" : "",
    emergencyContactName: contact?.emergencyContactName ?? "",
    emergencyContactPhone: contact?.emergencyContactPhone ?? "",
    fleetName: isFleet ? vehicle?.fleetName ?? "" : "",
    make: vehicle?.make ?? "",
    medicalConditions: medicalProfile?.medicalConditions ?? "",
    medications: medicalProfile?.medications ?? "",
    model: vehicle?.model ?? "",
    ownerEmail: getPrimaryEmail(profile),
    ownerName: getPrimaryName(profile),
    ownerPhone: getPrimaryPhone(profile),
    plateNumber: vehicle?.plateNumber ?? "",
    responderNotes: medicalProfile?.responderNotes ?? "",
    vehicleType: vehicle?.type ?? "",
    vin: isFleet ? "" : vehicle?.vin ?? "",
    year:
      typeof vehicle?.year === "number" && Number.isFinite(vehicle.year)
        ? String(vehicle.year)
        : "",
  }
}

export function buildOwnerProfileUpdateRequest(
  profile: OwnerPortalProfile | null,
  form: OwnerPortalFormState
): OwnerProfileUpdateRequest {
  const tagType = profile?.tagType ?? "vehicle_sticker"

  if (tagType === "personal_tag") {
    return {
      allergies: nullableString(form.allergies),
      bloodType: nullableString(form.bloodType),
      email: nullableString(form.ownerEmail),
      emergencyContactName: nullableString(form.emergencyContactName),
      emergencyContactPhone: nullableString(form.emergencyContactPhone),
      fullName: nullableString(form.ownerName),
      medicalConditions: nullableString(form.medicalConditions),
      medications: nullableString(form.medications),
      phone: nullableString(form.ownerPhone),
      responderNotes: nullableString(form.responderNotes),
    }
  }

  if (tagType === "fleet_tag") {
    return {
      assigneeEmail: nullableString(form.assigneeEmail),
      assigneeName: nullableString(form.assigneeName),
      assigneePhone: nullableString(form.assigneePhone),
      companyName: nullableString(form.companyName),
      contactEmail: nullableString(form.ownerEmail),
      contactName: nullableString(form.ownerName),
      contactPhone: nullableString(form.ownerPhone),
      fleetName: nullableString(form.fleetName),
      plateNumber: nullableString(form.plateNumber),
      vehicleColor: nullableString(form.color),
      vehicleMake: nullableString(form.make),
      vehicleModel: nullableString(form.model),
      vehicleType: nullableString(form.vehicleType),
      vehicleYear: nullableInteger(form.year),
    }
  }

  return {
    allergies: nullableString(form.allergies),
    bloodType: nullableString(form.bloodType),
    emergencyContactName: nullableString(form.emergencyContactName),
    emergencyContactPhone: nullableString(form.emergencyContactPhone),
    medicalConditions: nullableString(form.medicalConditions),
    medications: nullableString(form.medications),
    ownerEmail: nullableString(form.ownerEmail),
    ownerName: nullableString(form.ownerName),
    ownerPhone: nullableString(form.ownerPhone),
    plateNumber: nullableString(form.plateNumber),
    responderNotes: nullableString(form.responderNotes),
    vehicleColor: nullableString(form.color),
    vehicleMake: nullableString(form.make),
    vehicleModel: nullableString(form.model),
    vehicleType: nullableString(form.vehicleType),
    vehicleYear: nullableInteger(form.year),
    vin: nullableString(form.vin),
  }
}

export function createOwnerOtpChallenge(input: {
  debugOtp?: string | null
  expiresInSec?: number | null
  phoneNumber: string
  resendAfterSec?: number | null
  tag: string
  tagType?: TagType | null
  verificationId: string
}): OwnerOtpChallenge {
  const now = Date.now()

  return {
    debugOtp: input.debugOtp?.trim() ?? "",
    expiresAt: now + Math.max(input.expiresInSec ?? 0, 0) * 1000,
    phoneNumber: input.phoneNumber.trim(),
    resendAvailableAt: now + Math.max(input.resendAfterSec ?? 0, 0) * 1000,
    tag: input.tag.trim(),
    tagType: input.tagType ?? null,
    verificationId: input.verificationId,
  }
}

export function getOwnerContactLabels(tagType: TagType | null): OwnerContactLabels {
  if (tagType === "fleet_tag") {
    return {
      description:
        "Keep the primary contact path accurate for the fleet or assigned device owner.",
      email: "Primary contact email",
      name: "Primary contact name",
      phone: "Primary contact phone",
    }
  }

  if (tagType === "personal_tag") {
    return {
      description: "Keep the primary contact path accurate for verified follow-up.",
      email: "Email",
      name: "Full name",
      phone: "Phone",
    }
  }

  return {
    description: "Keep the primary contact path accurate for verified follow-up.",
    email: "Owner email",
    name: "Owner name",
    phone: "Owner phone",
  }
}

export function buildOwnerDetailRows(profile: OwnerPortalProfile | null): OwnerDetailRow[] {
  if (!profile) {
    return []
  }

  const rows: OwnerDetailRow[] = [
    {
      label: "Profile status",
      value: titleize(profile.device?.status ?? "active"),
    },
    {
      label: "Tag type",
      value: formatTagType(profile.tagType),
    },
    {
      label: "Vehicle reference",
      value: firstNonEmpty(
        profile.vehicle?.plateNumber,
        [profile.vehicle?.make, profile.vehicle?.model].filter(Boolean).join(" "),
        "Not provided"
      ),
    },
  ]

  if (profile.tagType === "fleet_tag") {
    rows.push({
      label: "Fleet",
      value: firstNonEmpty(
        profile.vehicle?.fleetName,
        profile.vehicle?.companyName,
        "Not provided"
      ),
    })
    rows.push({
      label: "Assignee",
      value: firstNonEmpty(profile.assignment?.assigneeName, "Not assigned"),
    })

    return rows
  }

  if (profile.tagType === "personal_tag") {
    rows.push({
      label: "Emergency contact",
      value: firstNonEmpty(
        joinLabelledValues([
          profile.contact?.emergencyContactName ?? "",
          profile.contact?.emergencyContactPhone ?? "",
        ]),
        "Not provided"
      ),
    })

    return rows
  }

  rows.push({
    label: "Vehicle type",
    value: firstNonEmpty(profile.vehicle?.type, "Not provided"),
  })
  rows.push({
    label: "Emergency contact",
    value: firstNonEmpty(
      joinLabelledValues([
        profile.contact?.emergencyContactName ?? "",
        profile.contact?.emergencyContactPhone ?? "",
      ]),
      "Not provided"
    ),
  })

  return rows
}

export function getPrimaryName(profile: OwnerPortalProfile | null): string {
  if (!profile) {
    return ""
  }

  return firstNonEmpty(
    profile.contact?.name,
    profile.assignment?.assigneeName,
    ""
  )
}

export function getPrimaryPhone(profile: OwnerPortalProfile | null): string {
  if (!profile) {
    return ""
  }

  return firstNonEmpty(
    profile.contact?.phone,
    profile.assignment?.assigneePhone,
    ""
  )
}

export function getPrimaryEmail(profile: OwnerPortalProfile | null): string {
  if (!profile) {
    return ""
  }

  return firstNonEmpty(
    profile.contact?.email,
    profile.assignment?.assigneeEmail,
    ""
  )
}

export function formatTagType(value: TagType | null): string {
  if (value === "vehicle_sticker") {
    return "Vehicle sticker"
  }

  if (value === "personal_tag") {
    return "Personal tag"
  }

  if (value === "fleet_tag") {
    return "Fleet tag"
  }

  return "Unknown"
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return "Not available"
  }

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) {
    return "Not available"
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed)
}

export function formatPhone(value: string | null | undefined): string {
  return value?.trim() || "No phone on file"
}

export function formatOwnerPortalStateLabel(
  value: string | null | undefined
): string {
  return value?.trim() ? titleize(value) : "Not available"
}

export function buildOwnerPortalGrievancePath(tag: string | null | undefined): string {
  return buildPublicGrievanceHref({
    caseType: "correction_help",
    reason: "owner_auth_unreachable_phone",
    source: "my_device_owner_auth",
    tag,
  })
}

export function formatCountdown(targetTimeMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil((targetTimeMs - Date.now()) / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
}

export function getOwnerIncidentTitle(incident: OwnerIncidentRecord): string {
  if (incident.reportKind === "behavior") {
    return titleize(incident.behaviorType ?? incident.type ?? "behavior")
  }

  return titleize(incident.type ?? "incident")
}

export function getOwnerIncidentSubjectLabel(incident: OwnerIncidentRecord): string {
  if (incident.subjectAttribution?.label?.trim()) {
    return incident.subjectAttribution.label
  }

  switch (incident.subjectType) {
    case "vehicle":
      return "Vehicle"
    case "driver_of_vehicle":
      return "Driver of vehicle"
    case "person":
      return "Person"
    default:
      return "Incident scene"
  }
}

export function getOwnerIncidentLocation(incident: OwnerIncidentRecord): string {
  return firstNonEmpty(
    incident.location?.name,
    incident.locationDescription,
    incident.location?.address,
    "Location not provided"
  )
}

export function getOwnerIncidentSummary(incident: OwnerIncidentRecord): string {
  if (incident.subjectAttribution?.summary?.trim()) {
    return incident.subjectAttribution.summary
  }

  switch (incident.subjectType) {
    case "vehicle":
      return "Vehicle remains the primary subject. Registered owner/contact details are follow-up context only."
    case "driver_of_vehicle":
      return "Observed driver behavior is the subject. The registered owner is not treated as the default behavior subject."
    case "person":
      return "The person linked to this tag remains the subject for follow-up."
    default:
      return "The report is attached to the incident scene context."
  }
}

export function getOwnerIncidentMediaSummary(
  incident: OwnerIncidentRecord
): string | null {
  const summary: string[] = []

  if (incident.incidentMedia.length > 0) {
    summary.push(
      `${incident.incidentMedia.length} photo${incident.incidentMedia.length === 1 ? "" : "s"}`
    )
  }

  if (incident.updates.length > 0) {
    summary.push(
      `${incident.updates.length} update${incident.updates.length === 1 ? "" : "s"}`
    )
  }

  return summary.length > 0 ? summary.join(" • ") : null
}

export function normalizePhoneInput(value: string): string {
  const trimmed = value.trim()

  if (!trimmed) {
    return ""
  }

  if (trimmed.startsWith("+")) {
    return trimmed
  }

  const digitsOnly = trimmed.replace(/[^\d]/g, "")
  return digitsOnly ? `+${digitsOnly}` : ""
}

function nullableString(value: string): string | null {
  const trimmed = value.trim()
  return trimmed || null
}

function nullableInteger(value: string): number | null {
  const trimmed = value.trim()

  if (!trimmed) {
    return null
  }

  const parsed = Number.parseInt(trimmed, 10)
  return Number.isFinite(parsed) ? parsed : null
}

function titleize(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase())
}

function firstNonEmpty(...values: Array<string | null | undefined>): string {
  return values.find((value) => typeof value === "string" && value.trim()) ?? ""
}

function joinLabelledValues(values: Array<string | null | undefined>): string {
  return values
    .filter(
      (value): value is string =>
        typeof value === "string" && Boolean(value.trim())
    )
    .join(" • ")
}
